import { useMemo } from 'react';
import { Offer, Product, usePlan, useSessionLinks } from '../queries';
import { useCountryPriceTiers } from '@app/api';
import { areSameProducts } from './products';
import {
  computeFees,
  computeIncentive,
  getSimpleOfferPrice,
  LazyProduct,
  LazyProductArray,
  NO_PRICE_GROUP,
  toProductArr,
} from './priceComputation';

export const useDefaultSessionLink = (sessionId?: string) => {
  const { data: sessionLinks } = useSessionLinks(sessionId);

  return useMemo(() => {
    if (!sessionLinks) {
      return undefined;
    }
    let defaultSessionLink = sessionLinks.find(
      (s) => s.defaultLink && !!s.recruitmentByTandemz,
    );
    if (!defaultSessionLink) {
      defaultSessionLink = sessionLinks.find((s) => s.defaultLink);
    }
    return defaultSessionLink || undefined;
  }, [sessionLinks]);
};

export const useRelevantPlanProducts = (
  planId?: string,
  sessionId?: string,
  countryKeys?: string[],
) => {
  const link = useDefaultSessionLink(sessionId);
  const { data: plan } = usePlan(planId);
  let countryPriceGroupIds: string[] | undefined =
    useCountryPriceTiers(countryKeys);

  if (!countryKeys) {
    countryPriceGroupIds = undefined;
  }

  return useMemo(
    () =>
      getRelevantProducts(plan?.products, {
        sessionLinkId: link?.id,
        countryPriceGroupIds,
      }),
    [plan, link, countryPriceGroupIds],
  );
};

export const filterAlreadySelected = <T extends LazyProduct>(
  products: T[],
  alreadySelected?: LazyProductArray,
): T[] => {
  if (!alreadySelected) {
    return products;
  }
  if (!products) {
    return [];
  }
  const alreadySelectedProducts = toProductArr(alreadySelected);

  return products.filter((op) => {
    return !alreadySelectedProducts.some((p) => {
      if ('type' in op) {
        return areSameProducts(op, p);
      }
      if ('product' in op && op.product) {
        return areSameProducts(op.product, p);
      }
      return false;
    });
  });
};

export const getRelevantProducts = <
  T extends {
    product?: Product;
    forRecruitmentByClient?: boolean;
    sessionLinkId?: string;
    countryPriceGroupId?: string;
  },
>(
  products: T[] | undefined,
  {
    countryPriceGroupIds,
    sessionLinkId: currentSessionLinkId,
  }: {
    countryPriceGroupIds?: string[];
    sessionLinkId?: string;
  },
  alreadySelectedProducts: LazyProductArray = [],
): T[] => {
  if (!products) {
    return [];
  }
  return filterAlreadySelected(products, alreadySelectedProducts).filter(
    ({ forRecruitmentByClient, countryPriceGroupId, sessionLinkId }) => {
      if (forRecruitmentByClient) {
        return false;
      }
      if (countryPriceGroupId && countryPriceGroupIds) {
        if (!countryPriceGroupIds.includes(countryPriceGroupId)) {
          return false;
        }
      }

      if (currentSessionLinkId) {
        if (sessionLinkId && sessionLinkId !== currentSessionLinkId) {
          return false;
        }
      }
      return true;
    },
  );
};

export const getGroupProducts = <
  T extends {
    product?: Product;
    countryPriceGroupId?: string;
  },
>(
  products: T[] | undefined,
  group: string,
): T[] => {
  if (group === NO_PRICE_GROUP) {
    return products || [];
  }
  return getRelevantProducts(products, {
    countryPriceGroupIds: [group],
  });
};
export const getMeanFeesForOffer = (offer: Offer) => {
  const productsByFilters: Record<string, Product[]> = {};
  for (const prod of offer.products) {
    const key = `${prod.countryPriceGroupId}`;
    if (!prod.product) {
      continue;
    }
    if (!productsByFilters[key]) {
      productsByFilters[key] = [];
    }
    productsByFilters[key].push(prod.product);
  }
  let feesCount = 0;
  let sum = 0;
  for (const products of Object.values(productsByFilters)) {
    const incentive = computeIncentive(products);
    const fees = computeFees(products, 60, incentive);
    if (fees > 0) {
      feesCount++;
      sum += fees;
    }
  }
  return sum / feesCount;
};

export const getSupportOfferPrice = (
  offer: Offer,
  alreadySelectedProducts: Product[] = [],
) => {
  const { amount } = getSimpleOfferPrice(
    filterAlreadySelected(offer.products, alreadySelectedProducts),
  );
  return amount;
};
